import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useEntityNameLookup, type EntityNameLookup } from "../entityNames";

// "extra_instructions" -> "Extra instructions": arg labels read as human
// field names wherever approval details render.
export function humanizeFieldLabel(key: string): string {
  const words = key.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Raw id args mean nothing to the user — show the entity's name when the
// widget's name maps can resolve it ("agent_id: e722…" -> "Agent: Tony").
export function resolveIdField(
  names: EntityNameLookup,
  key: string,
  value: unknown
): { label: string; name: string } | null {
  if (typeof value !== "string" || !value) return null;
  if (key === "agent_id") {
    const name = names.agentNameFor(value);
    return name ? { label: "Agent", name } : null;
  }
  if (key === "kb_id" || key === "knowledge_base_id") {
    const name = names.kbNameFor(value);
    return name ? { label: "Knowledge base", name } : null;
  }
  return null;
}

export interface ArgsApprovalPlan {
  entries: [string, unknown][];
}

/** Fallback plan for any approval-gated tool without a structured renderer:
 * the call's arguments as labeled fields. */
export function parseArgsApprovalPlan(
  _toolName: string,
  input: unknown
): ArgsApprovalPlan | null {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return null;
  }
  const entries = Object.entries(input as Record<string, unknown>);
  return entries.length > 0 ? { entries } : null;
}

export function ArgsApprovalBody({
  plan,
  warning,
}: {
  plan: ArgsApprovalPlan | null;
  // The destructive-tool warning, for a tool that declares no steps.
  warning?: string;
}) {
  const names = useEntityNameLookup();
  return (
    <div className="space-y-2.5 text-xs">
      {(plan?.entries ?? []).map(([key, value]) => {
        const named = resolveIdField(names, key, value);
        return (
          <div key={key} className="flex flex-col gap-0.5">
            <span className="text-foreground font-medium">
              {named ? named.label : humanizeFieldLabel(key)}
            </span>
            <span className="text-foreground/80 break-words line-clamp-2">
              {named
                ? named.name
                : typeof value === "string"
                  ? value
                  : JSON.stringify(value)}
            </span>
          </div>
        );
      })}
      {warning && (
        <NoxusAlert variant="destructive" size="xs" title={warning} />
      )}
    </div>
  );
}
